// src/pages/finance-portal/dashboard/AccountSetupWizard.js
// Multi-step wizard for connecting bank accounts via Stripe Financial Connections

import React, { useState } from 'react';
import {
    Box,
    Typography,
    Stepper,
    Step,
    StepLabel,
    Button,
    Card,
    CardContent,
    Checkbox,
    Alert,
    CircularProgress,
    Chip,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SavingsIcon from '@mui/icons-material/Savings';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import StripeLinkButton from '../components/StripeLinkButton';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const steps = [
    { label: 'Connect Bank', slug: 'connect-bank' },
    { label: 'Select Accounts', slug: 'select-accounts' },
    { label: 'Review & Confirm', slug: 'review-confirm' },
];

const ACCOUNT_TYPE_ICONS = {
    DEPOSITORY: <SavingsIcon />,
    CREDIT: <CreditCardIcon />,
    LOAN: <ReceiptLongIcon />,
    INVESTMENT: <TrendingUpIcon />,
    OTHER: <AccountBalanceWalletIcon />,
};

const ACCOUNT_TYPE_COLORS = {
    DEPOSITORY: '#22c55e',
    CREDIT: '#f59e0b',
    LOAN: '#ef4444',
    INVESTMENT: '#8b5cf6',
    OTHER: '#64748b',
};

function formatCurrency(amount, currency = 'USD') {
    if (amount == null) return '—';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'USD',
    }).format(amount);
}

export default function AccountSetupWizard({ onSetupComplete }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [activeStep, setActiveStep] = useState(0);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    // Step 1 state
    const [connectedItem, setConnectedItem] = useState(null);
    const [accounts, setAccounts] = useState([]);

    // Step 2 state
    const [selectedAccountIds, setSelectedAccountIds] = useState(new Set());

    // Step 1: Handle Stripe Financial Connections success
    const handleStripeSuccess = async (sessionId, metadata) => {
        setLoading(true);
        setError(null);
        try {
            const response = await axiosInstance.post('/api/finance/stripe/complete-connection', {
                session_id: sessionId,
            });

            if (response.data.success) {
                setConnectedItem(response.data.item);
                setAccounts(response.data.accounts);
                // Select all accounts by default
                setSelectedAccountIds(new Set(response.data.accounts.map(a => a.id)));
                setActiveStep(1);
            } else {
                setError(response.data.message || 'Failed to connect bank');
            }
        } catch (err) {
            const msg = err.response?.data?.message || err.message || 'Failed to connect bank';
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    // Step 2: Toggle account selection
    const toggleAccount = (accountId) => {
        setSelectedAccountIds(prev => {
            const next = new Set(prev);
            if (next.has(accountId)) {
                next.delete(accountId);
            } else {
                next.add(accountId);
            }
            return next;
        });
    };

    const toggleAll = () => {
        if (selectedAccountIds.size === accounts.length) {
            setSelectedAccountIds(new Set());
        } else {
            setSelectedAccountIds(new Set(accounts.map(a => a.id)));
        }
    };

    // Step 2 → 3: Save selections
    const handleSaveSelections = async () => {
        setLoading(true);
        setError(null);
        try {
            // Deselect unchecked accounts
            const deselectedIds = accounts
                .filter(a => !selectedAccountIds.has(a.id))
                .map(a => a.id);

            if (deselectedIds.length > 0) {
                await axiosInstance.post('/api/finance/stripe/accounts/select', {
                    account_ids: deselectedIds,
                    selected: false,
                });
            }

            setActiveStep(2);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save account selections');
        } finally {
            setLoading(false);
        }
    };

    // Step 3: Complete setup
    const handleComplete = () => {
        if (onSetupComplete) {
            onSetupComplete();
        }
    };

    const selectedAccounts = accounts.filter(a => selectedAccountIds.has(a.id));

    // Custom step icon
    const StepIconComponent = ({ active, completed, icon }) => {
        return (
            <Box
                sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: completed ? ACCENT : active ? ACCENT : '#e2e8f0',
                    color: completed || active ? '#fff' : '#94a3b8',
                    fontFamily,
                    fontWeight: 700,
                    fontSize: '0.85rem',
                    transition: 'all 0.3s ease',
                }}
            >
                {completed ? <CheckCircleIcon sx={{ fontSize: 20 }} /> : icon}
            </Box>
        );
    };

    return (
        <Box sx={{ maxWidth: 800, mx: 'auto' }}>
            {/* Stepper */}
            <Box
                sx={{
                    bgcolor: 'rgba(255,255,255,0.7)',
                    backdropFilter: 'blur(12px)',
                    borderRadius: '16px',
                    border: '1px solid rgba(0,0,0,0.06)',
                    p: { xs: 2, md: 3 },
                    mb: 3,
                }}
            >
                <Stepper
                    activeStep={activeStep}
                    alternativeLabel={!isMobile}
                    orientation={isMobile ? 'vertical' : 'horizontal'}
                    sx={{
                        '& .MuiStepConnector-line': {
                            borderColor: '#e2e8f0',
                        },
                    }}
                >
                    {steps.map((step, index) => (
                        <Step key={step.slug} completed={index < activeStep}>
                            <StepLabel
                                StepIconComponent={(props) => (
                                    <StepIconComponent {...props} icon={index + 1} />
                                )}
                            >
                                <Typography sx={{
                                    fontFamily,
                                    fontWeight: index <= activeStep ? 600 : 400,
                                    fontSize: '0.85rem',
                                    color: index <= activeStep ? '#1e293b' : '#94a3b8',
                                }}>
                                    {step.label}
                                </Typography>
                            </StepLabel>
                        </Step>
                    ))}
                </Stepper>
            </Box>

            {/* Error Alert */}
            {error && (
                <Alert
                    severity="error"
                    onClose={() => setError(null)}
                    sx={{ mb: 2, borderRadius: '12px', fontFamily }}
                >
                    {error}
                </Alert>
            )}

            {/* Step Content */}
            <Box
                sx={{
                    bgcolor: 'rgba(255,255,255,0.7)',
                    backdropFilter: 'blur(12px)',
                    borderRadius: '16px',
                    border: '1px solid rgba(0,0,0,0.06)',
                    p: { xs: 2.5, md: 4 },
                }}
            >
                {/* Step 1: Connect Bank */}
                {activeStep === 0 && (
                    <Box sx={{ textAlign: 'center', py: 3 }}>
                        <Box
                            sx={{
                                width: 80,
                                height: 80,
                                borderRadius: '20px',
                                bgcolor: 'rgba(34, 197, 94, 0.1)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mx: 'auto',
                                mb: 3,
                            }}
                        >
                            <AccountBalanceIcon sx={{ fontSize: 40, color: ACCENT }} />
                        </Box>
                        <Typography sx={{
                            fontFamily,
                            fontWeight: 700,
                            fontSize: '1.3rem',
                            color: '#1e293b',
                            mb: 1,
                        }}>
                            Connect Your Bank Account
                        </Typography>
                        <Typography sx={{
                            fontFamily,
                            fontSize: '0.95rem',
                            color: '#64748b',
                            mb: 4,
                            maxWidth: 500,
                            mx: 'auto',
                        }}>
                            Securely connect your bank accounts to automatically import transactions,
                            track balances, and manage your finances in one place.
                        </Typography>

                        {loading ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                <CircularProgress sx={{ color: ACCENT }} size={48} />
                                <Typography sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '1rem' }}>
                                    Connecting your bank account...
                                </Typography>
                                <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.85rem' }}>
                                    Fetching account details and balances. This may take a few seconds.
                                </Typography>
                            </Box>
                        ) : (
                            <StripeLinkButton
                                onSuccess={handleStripeSuccess}
                                buttonText="Connect Bank Account"
                            />
                        )}

                        <Typography sx={{
                            fontFamily,
                            fontSize: '0.75rem',
                            color: '#94a3b8',
                            mt: 3,
                        }}>
                            Your credentials are encrypted and never stored on our servers.
                            Powered by Stripe, trusted by millions.
                        </Typography>
                    </Box>
                )}

                {/* Step 2: Select Accounts */}
                {activeStep === 1 && (
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                            <Box>
                                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem', color: '#1e293b' }}>
                                    Select Accounts to Track
                                </Typography>
                                <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b', mt: 0.5 }}>
                                    {connectedItem?.institution_name} - Choose which accounts to monitor
                                </Typography>
                            </Box>
                            <Button
                                size="small"
                                onClick={toggleAll}
                                sx={{ fontFamily, textTransform: 'none', color: ACCENT }}
                            >
                                {selectedAccountIds.size === accounts.length ? 'Deselect All' : 'Select All'}
                            </Button>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {accounts.map((account) => {
                                const isSelected = selectedAccountIds.has(account.id);
                                const typeColor = ACCOUNT_TYPE_COLORS[account.type] || '#64748b';
                                const typeIcon = ACCOUNT_TYPE_ICONS[account.type] || ACCOUNT_TYPE_ICONS.OTHER;

                                return (
                                    <Card
                                        key={account.id}
                                        onClick={() => toggleAccount(account.id)}
                                        sx={{
                                            cursor: 'pointer',
                                            borderRadius: '12px',
                                            border: isSelected ? `2px solid ${ACCENT}` : '2px solid #e2e8f0',
                                            boxShadow: 'none',
                                            transition: 'all 0.2s ease',
                                            bgcolor: isSelected ? 'rgba(34, 197, 94, 0.04)' : '#fff',
                                            '&:hover': {
                                                borderColor: isSelected ? ACCENT : '#cbd5e1',
                                                transform: 'translateY(-1px)',
                                            },
                                        }}
                                    >
                                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Checkbox
                                                checked={isSelected}
                                                sx={{
                                                    color: '#cbd5e1',
                                                    '&.Mui-checked': { color: ACCENT },
                                                }}
                                            />
                                            <Box sx={{
                                                width: 44,
                                                height: 44,
                                                borderRadius: '12px',
                                                bgcolor: `${typeColor}15`,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: typeColor,
                                                flexShrink: 0,
                                            }}>
                                                {typeIcon}
                                            </Box>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography sx={{
                                                    fontFamily,
                                                    fontWeight: 600,
                                                    fontSize: '0.9rem',
                                                    color: '#1e293b',
                                                }}>
                                                    {account.name}
                                                    {account.mask && (
                                                        <Typography component="span" sx={{
                                                            fontFamily,
                                                            fontSize: '0.8rem',
                                                            color: '#94a3b8',
                                                            ml: 1,
                                                        }}>
                                                            ****{account.mask}
                                                        </Typography>
                                                    )}
                                                </Typography>
                                                <Chip
                                                    label={account.subtype || account.type}
                                                    size="small"
                                                    sx={{
                                                        fontFamily,
                                                        fontSize: '0.7rem',
                                                        fontWeight: 600,
                                                        height: 22,
                                                        bgcolor: `${typeColor}15`,
                                                        color: typeColor,
                                                        mt: 0.5,
                                                    }}
                                                />
                                            </Box>
                                            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                                                <Typography sx={{
                                                    fontFamily,
                                                    fontWeight: 700,
                                                    fontSize: '1rem',
                                                    color: (account.current_balance || 0) >= 0 ? '#1e293b' : '#ef4444',
                                                }}>
                                                    {formatCurrency(account.current_balance, account.iso_currency_code)}
                                                </Typography>
                                                {account.available_balance != null && account.available_balance !== account.current_balance && (
                                                    <Typography sx={{
                                                        fontFamily,
                                                        fontSize: '0.75rem',
                                                        color: '#94a3b8',
                                                    }}>
                                                        Available: {formatCurrency(account.available_balance, account.iso_currency_code)}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </Box>

                        {/* Navigation */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                            <Button
                                onClick={() => setActiveStep(0)}
                                startIcon={<ArrowBackIcon />}
                                sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}
                            >
                                Back
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleSaveSelections}
                                disabled={selectedAccountIds.size === 0 || loading}
                                endIcon={loading ? <CircularProgress size={16} color="inherit" /> : <ArrowForwardIcon />}
                                sx={{
                                    fontFamily,
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    borderRadius: '12px',
                                    px: 3,
                                    bgcolor: ACCENT,
                                    '&:hover': { bgcolor: '#16a34a' },
                                }}
                            >
                                Continue ({selectedAccountIds.size} selected)
                            </Button>
                        </Box>
                    </Box>
                )}

                {/* Step 3: Review & Confirm */}
                {activeStep === 2 && (
                    <Box>
                        <Box sx={{ textAlign: 'center', mb: 3 }}>
                            <CheckCircleIcon sx={{ fontSize: 56, color: ACCENT, mb: 1 }} />
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.2rem', color: '#1e293b' }}>
                                Review Your Setup
                            </Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#64748b', mt: 0.5 }}>
                                Confirm the accounts you want to track
                            </Typography>
                        </Box>

                        {/* Institution Card */}
                        <Card sx={{
                            borderRadius: '12px',
                            border: '1px solid #e2e8f0',
                            boxShadow: 'none',
                            mb: 2,
                        }}>
                            <CardContent sx={{ p: 2.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                    <Box sx={{
                                        width: 48,
                                        height: 48,
                                        borderRadius: '12px',
                                        bgcolor: 'rgba(34, 197, 94, 0.1)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}>
                                        <AccountBalanceIcon sx={{ color: ACCENT }} />
                                    </Box>
                                    <Box>
                                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>
                                            {connectedItem?.institution_name || 'Bank'}
                                        </Typography>
                                        <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b' }}>
                                            {selectedAccounts.length} account{selectedAccounts.length !== 1 ? 's' : ''} selected
                                        </Typography>
                                    </Box>
                                </Box>

                                {selectedAccounts.map((account, idx) => (
                                    <Box
                                        key={account.id}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            py: 1.5,
                                            borderTop: idx > 0 ? '1px solid #f1f5f9' : 'none',
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Box sx={{
                                                color: ACCOUNT_TYPE_COLORS[account.type] || '#64748b',
                                                display: 'flex',
                                            }}>
                                                {ACCOUNT_TYPE_ICONS[account.type] || ACCOUNT_TYPE_ICONS.OTHER}
                                            </Box>
                                            <Box>
                                                <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: '#1e293b' }}>
                                                    {account.name} {account.mask ? `****${account.mask}` : ''}
                                                </Typography>
                                                <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8' }}>
                                                    {account.subtype || account.type}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Typography sx={{
                                            fontFamily,
                                            fontWeight: 600,
                                            fontSize: '0.9rem',
                                            color: (account.current_balance || 0) >= 0 ? '#1e293b' : '#ef4444',
                                        }}>
                                            {formatCurrency(account.current_balance, account.iso_currency_code)}
                                        </Typography>
                                    </Box>
                                ))}
                            </CardContent>
                        </Card>

                        {/* Navigation */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                            <Button
                                onClick={() => setActiveStep(1)}
                                startIcon={<ArrowBackIcon />}
                                sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}
                            >
                                Back
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleComplete}
                                endIcon={<CheckCircleIcon />}
                                sx={{
                                    fontFamily,
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    borderRadius: '12px',
                                    px: 4,
                                    py: 1.5,
                                    bgcolor: ACCENT,
                                    '&:hover': { bgcolor: '#16a34a' },
                                }}
                            >
                                Complete Setup
                            </Button>
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    );
}
